import { getRaf } from "@app";
import { clamp, map } from "@utils/math";

import "./style.css";

export default class Carousel {
  static name = "Carousel";

  constructor(block) {
    this.block = block;
    this.itemNodes = this.block.querySelectorAll(".carousel__item");
    this.imagesNode = this.block.querySelector(".carousel__items");
    this.itemContentNodes = this.block.querySelectorAll(
      ".carousel__item__content"
    );
    this.imageNodes = this.block.querySelectorAll(
      ".carousel__item__content__image"
    );
    this.textNodes = this.block.querySelectorAll(
      ".carousel__item__content__text"
    );

    this.index = 0;
    this.onIndexUpdateCallbacks = [];
    this.animations = {
      itemsNode: {
        node: this.imagesNode,
        styles: {
          transform: {
            style: () => {
              const value = this.animations.itemsNode.styles.transform.current;

              return `translate3d(${value}px, 0, 0)`;
            },
            dampingFactor: 0.09,
            deltaX: 0,
            deltaX0: 0,
            fromValue: 0,
            setValue: () => {
              this.animations.itemsNode.styles.transform.deltaX0 =
                this.animations.itemsNode.styles.transform.deltaX0 +
                (this.animations.itemsNode.styles.transform.deltaX -
                  this.animations.itemsNode.styles.transform.deltaX0) *
                  this.animations.itemsNode.styles.transform.dampingFactor;

              return this.animations.itemsNode.styles.transform.deltaX0;
            },
          },
        },
      },
    };

    this.itemContentNodes.forEach((node, index) => {
      this.animations[`item_${index}`] = {
        node: node,
        styles: {
          transform: {
            style: () => {
              const value =
                this.animations[`item_${index}`].styles.transform.current;

              return `scale3d(${value}, ${value}, 1)`;
            },
            dampingFactor: 0.09,
            toValue: 1,
            fromValue: 1,
            current: 1,
            setValue: () => {
              this.animations[`item_${index}`].styles.transform.fromValue =
                this.animations[`item_${index}`].styles.transform.fromValue +
                (this.animations[`item_${index}`].styles.transform.toValue -
                  this.animations[`item_${index}`].styles.transform.fromValue) *
                  this.animations[`item_${index}`].styles.transform
                    .dampingFactor;

              return this.animations[`item_${index}`].styles.transform
                .fromValue;
            },
          },
        },
      };
    });

    this.imageNodes.forEach((node, index) => {
      this.animations[`image_${index}`] = {
        node: node,
        styles: {
          transform: {
            style: () => {
              const value =
                this.animations[`image_${index}`].styles.transform.current;

              return `translate3d(${this.getInnerTranslation()}px, 0, 0) scale3d(${value}, ${value}, 1)`;
            },
            dampingFactor: 0.09,
            toValue: 1,
            fromValue: 1,
            current: 1,
            setValue: () => {
              this.animations[`image_${index}`].styles.transform.fromValue =
                this.animations[`image_${index}`].styles.transform.fromValue +
                (this.animations[`image_${index}`].styles.transform.toValue -
                  this.animations[`image_${index}`].styles.transform
                    .fromValue) *
                  this.animations[`image_${index}`].styles.transform
                    .dampingFactor;

              return this.animations[`image_${index}`].styles.transform
                .fromValue;
            },
          },
        },
      };
    });

    this.textNodes.forEach((node, index) => {
      this.animations[`text_${index}`] = {
        node: node,
        styles: {
          transform: {
            style: () => {
              const value =
                this.animations[`text_${index}`].styles.transform.current;

              return `translate3d(${this.getInnerTranslation()}px, 0, 0) scale3d(${value}, ${value}, 1)`;
            },
            dampingFactor: 0.09,
            toValue: 1,
            fromValue: 1,
            current: 1,
            setValue: () => {
              this.animations[`text_${index}`].styles.transform.fromValue =
                this.animations[`text_${index}`].styles.transform.fromValue +
                (this.animations[`text_${index}`].styles.transform.toValue -
                  this.animations[`text_${index}`].styles.transform.fromValue) *
                  this.animations[`text_${index}`].styles.transform
                    .dampingFactor;

              return this.animations[`text_${index}`].styles.transform
                .fromValue;
            },
          },
        },
      };
    });

    window.addEventListener("resize", this.onResize);
    this.block.addEventListener("touchstart", this.onTouchStart, {
      passive: true,
    });
    this.block.addEventListener("touchmove", this.onTouchMove, {
      passive: true,
    });
    this.block.addEventListener("touchend", this.onTouchEnd, {
      passive: true,
    });
    this.block.addEventListener("mousedown", this.onTouchStart);
    this.block.addEventListener("mousemove", this.onTouchMove);
    this.block.addEventListener("mouseup", this.onTouchEnd);
    this.block.addEventListener("mouseleave", this.onMouseOut);

    this.setImagesRect();
    this.setImagesNodeMargin();
    this.onResize();
  }

  setImagesRect = () => {
    if (!this.itemNodes || this.itemNodes.length < 2) {
      return null;
    }

    this.itemNodes[0].rect = this.itemNodes[0].getBoundingClientRect();
    this.itemNodes[1].rect = this.itemNodes[1].getBoundingClientRect();

    this.imageMarginRight = this.getImageMarginRight();
    this.imageWidth = this.itemNodes[0].rect.width + this.imageMarginRight;
  };

  getImageMarginRight = () => {
    if (!this.itemNodes || this.itemNodes.length < 2) {
      return null;
    }

    return (
      this.itemNodes[1].rect.left -
      (this.itemNodes[0].rect.width + this.itemNodes[0].rect.left)
    );
  };

  setImagesNodeMargin = () => {
    if (!this.imagesNode) {
      return null;
    }

    this.imagesNodeMargin =
      this.imagesNode.offsetWidth -
      (this.imageWidth * this.itemNodes.length - this.imageMarginRight);
  };

  getInnerTranslation() {
    const distance =
      this.animations.itemsNode.styles.transform.current -
      this.animations.itemsNode.styles.transform.deltaX;

    const boundaries = {
      distance: { min: 0, max: 400 },
      translation: {
        min: 0,
        max:
          ((100 / 2) * this.animations["image_0"].styles.transform.current) /
          this.animations["item_0"].styles.transform.current,
      },
    };

    let translationVal = map(
      Math.abs(distance),
      boundaries.distance.min,
      boundaries.distance.max,
      boundaries.translation.min,
      boundaries.translation.max
    );
    translationVal = clamp(
      translationVal,
      boundaries.translation.min,
      boundaries.translation.max
    );

    return distance > 0
      ? translationVal.toFixed(2)
      : -1 * translationVal.toFixed(2);
  }

  updateIndex = () => {
    if (
      Math.abs(this.animations.itemsNode.styles.transform.dX) >
      this.imageWidth / 4
    ) {
      this.index =
        this.animations.itemsNode.styles.transform.dX < 0
          ? this.index - 1
          : this.index + 1;

      const itemsInView = Math.trunc(this.block.offsetWidth / this.imageWidth);
      this.index = clamp(this.index, -(this.itemNodes.length - itemsInView), 0);
    }

    this.animations.itemsNode.styles.transform.deltaX =
      this.animations.itemsNode.styles.transform.fromValue =
        this.index * this.imageWidth;
    this.animations.itemsNode.styles.transform.dX = 0;

    this.onIndexUpdateCallbacks.forEach((callback) => {
      callback(this.index);
    });
  };

  setIndex = (newIndex) => {
    this.index = clamp(newIndex, -this.itemNodes.length + 1, 0);
    this.animations.itemsNode.styles.transform.deltaX =
      this.animations.itemsNode.styles.transform.fromValue =
        this.index * this.imageWidth;
  };

  registerOnIndexUpdate = (callback) => {
    this.onIndexUpdateCallbacks.push(callback);
  };

  render = () => {
    for (const key1 in this.animations) {
      for (const key2 in this.animations[key1].styles) {
        this.animations[key1].styles[key2].current =
          this.animations[key1].styles[key2].setValue();
      }
    }

    this.layout();
  };

  layout = () => {
    for (const key1 in this.animations) {
      for (const key2 in this.animations[key1].styles) {
        this.animations[key1].node.style[key2] =
          this.animations[key1].styles[key2].style();
      }
    }
  };

  onTouchStart = (e) => {
    this.block.style.cursor = "grabbing";

    this.animations.itemsNode.styles.transform.x0 =
      (e.touches && e.touches[0] && e.touches[0].clientX) || e.clientX;

    this.itemContentNodes.forEach((node, index) => {
      this.animations[`item_${index}`].styles.transform.toValue = 0.9;
    });

    this.imageNodes.forEach((node, index) => {
      this.animations[`image_${index}`].styles.transform.toValue = 1.1;
    });

    this.textNodes.forEach((node, index) => {
      this.animations[`text_${index}`].styles.transform.toValue = 1.1;
    });
  };

  onTouchMove = (e) => {
    if (!this.animations.itemsNode.styles.transform.x0) {
      return;
    }

    this.animations.itemsNode.styles.transform.x =
      (e.touches && e.touches[0] && e.touches[0].clientX) || e.clientX;

    this.animations.itemsNode.styles.transform.dX =
      this.animations.itemsNode.styles.transform.x -
      this.animations.itemsNode.styles.transform.x0;

    this.animations.itemsNode.styles.transform.deltaX =
      this.animations.itemsNode.styles.transform.x -
      this.animations.itemsNode.styles.transform.x0 +
      this.animations.itemsNode.styles.transform.fromValue;
  };

  onTouchEnd = (e) => {
    this.block.style.cursor = null;

    this.itemContentNodes.forEach((node, index) => {
      this.animations[`item_${index}`].styles.transform.toValue = 1;
    });

    this.imageNodes.forEach((node, index) => {
      this.animations[`image_${index}`].styles.transform.toValue = 1;
    });

    this.textNodes.forEach((node, index) => {
      this.animations[`text_${index}`].styles.transform.toValue = 1;
    });

    if (
      !this.animations.itemsNode.styles.transform.x0 ||
      !this.animations.itemsNode.styles.transform.x
    ) {
      this.animations.itemsNode.styles.transform.x = null;
      this.animations.itemsNode.styles.transform.x0 = null;

      return;
    }

    this.updateIndex();

    this.animations.itemsNode.styles.transform.x = null;
    this.animations.itemsNode.styles.transform.x0 = null;
  };

  onMouseOut = () => {
    this.block.style.cursor = null;

    this.itemContentNodes.forEach((node, index) => {
      this.animations[`item_${index}`].styles.transform.toValue = 1;
    });

    this.imageNodes.forEach((node, index) => {
      this.animations[`image_${index}`].styles.transform.toValue = 1;
    });

    this.textNodes.forEach((node, index) => {
      this.animations[`text_${index}`].styles.transform.toValue = 1;
    });

    if (!this.animations.itemsNode.styles.transform.x0) {
      return;
    }

    this.updateIndex();

    this.animations.itemsNode.styles.transform.x0 = null;
    this.animations.itemsNode.styles.transform.x = null;
  };

  onResize = () => {
    this.setImagesRect();
    this.setImagesNodeMargin();
    this.updateIndex();

    if (!this.raf) {
      this.raf = getRaf();
      this.raf.register(this.block.dataset.instanceIndex, this.render);
    }
  };
}
